/**
 * dcg-cart.js — Desert City Games  v9
 *
 * §1  OUTSIDE-CLICK CLOSE   — Bootstrap 3 cart dropdown
 * §2  QTY STEPPER           — minus→0, max clamp, visual states
 * §3  PAYMENT BUTTONS       — DOM restructuring approach
 *
 * WHY DOM RESTRUCTURING (§3):
 *   Every CSS approach failed because the Shopify payment SDK
 *   dynamically injects its own <style> tag after our CSS loads.
 *   Two equally-specific !important rules resolve by SOURCE ORDER
 *   in the document — the SDK's rule is last, so it always wins.
 *
 *   The only reliable solution is to MOVE the button elements into
 *   a container WE own with an inline flex-row style. Inline styles
 *   override everything. Our container is never touched by the SDK.
 *
 *   DOM before:
 *     .additional-checkout-buttons
 *       > div (SDK wrapper, flex-direction:column)
 *         > .shopify-cleanslate (Shop Pay)
 *         > .shopify-cleanslate (Google Pay)
 *   OR:
 *     .additional-checkout-buttons
 *       > .shopify-cleanslate (contains both buttons in column UL)
 *
 *   DOM after:
 *     .additional-checkout-buttons
 *       > div.dcg-pay-row (OUR div, inline style: display:flex; flex-direction:row)
 *         > .shopify-cleanslate (Shop Pay)
 *         > .shopify-cleanslate (Google Pay)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     §1  OUTSIDE-CLICK CLOSE
     ───────────────────────────────────────────────────────── */
  function initOutsideClickClose() {
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.cart-display .cart-container.in')
        .forEach(function (dropdown) {
          var cd = dropdown.closest('.cart-display');
          if (!cd || cd.contains(e.target)) return;
          if (typeof $ !== 'undefined' && $.fn && $.fn.collapse) {
            $(dropdown).collapse('hide');
          } else {
            dropdown.classList.remove('in');
            var t = cd.querySelector('.cart-title');
            if (t) { t.classList.add('collapsed'); t.setAttribute('aria-expanded', 'false'); }
          }
        });
    }, false);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      document.querySelectorAll('.cart-display .cart-container.in')
        .forEach(function (d) {
          if (typeof $ !== 'undefined' && $.fn && $.fn.collapse) {
            $(d).collapse('hide');
          } else {
            d.classList.remove('in');
            var t = d.closest('.cart-display') && d.closest('.cart-display').querySelector('.cart-title');
            if (t) { t.classList.add('collapsed'); t.setAttribute('aria-expanded', 'false'); }
          }
        });
    });
  }

  /* ─────────────────────────────────────────────────────────
     §2  QTY STEPPER + TYPED-INPUT GUARD
     ─────────────────────────────────────────────────────────
     getEffectiveMax() combines two sources of truth:
       1. max="" attribute  — set from Shopify inventory in Liquid
       2. data-tags=""      — product tags parsed for limit-N / limit-word
     The lowest value wins. This means typing 9999 is caught
     the same way the +/- stepper buttons are caught.
     ───────────────────────────────────────────────────────── */

  // Word tags: limit-one … limit-ten (matches dcg-qty-guard.js)
  var WORD_TO_NUM = {
    one:1, two:2, three:3, four:4, five:5,
    six:6, seven:7, eight:8, nine:9, ten:10
  };

  function parseLimitFromTags(tagsAttr) {
    if (!tagsAttr) return null;
    // Prefer the shared parser (dcg-utils.js) — single source of truth.
    if (window.DCG && typeof window.DCG.parseLimit === 'function') {
      return window.DCG.parseLimit(tagsAttr);
    }
    var limits = [];
    var tags = tagsAttr.split(',');
    for (var i = 0; i < tags.length; i++) {
      var tag = tags[i].trim().toLowerCase();
      var m = tag.match(/^limit-(one|two|three|four|five|six|seven|eight|nine|ten)$/);
      if (m) {
        var w = WORD_TO_NUM[m[1]];
        if (w) limits.push(w);
        continue;
      }
      m = tag.match(/^limit-(\d{1,4})$/);
      if (m) {
        var n = parseInt(m[1], 10);
        if (n > 0) limits.push(n);
      }
    }
    return limits.length ? Math.min.apply(null, limits) : null;
  }

  function getEffectiveMax(input) {
    // Source 1: inventory stock from max attribute
    var stockMax = Infinity;
    var ma = input.getAttribute('max');
    if (ma) {
      var parsed = parseInt(ma, 10);
      if (!isNaN(parsed) && parsed > 0) stockMax = parsed;
    }

    // Source 2: limit-X product tags from data-tags attribute
    var limitMax = Infinity;
    var tagLimit = parseLimitFromTags(input.getAttribute('data-tags') || '');
    if (tagLimit !== null && tagLimit > 0) limitMax = tagLimit;

    var effective = Math.min(stockMax, limitMax);
    return isFinite(effective) ? effective : Infinity;
  }

  // Keep old name as alias for any callers inside this file
  function getMax(input) { return getEffectiveMax(input); }

  // Clamp an input to its effective max and show inline feedback if clamped.
  // Returns true if a clamp occurred.
  function clampCartInput(input) {
    var max = getEffectiveMax(input);
    if (!isFinite(max)) return false;

    var val = parseInt(input.value, 10);
    if (isNaN(val) || val <= max) return false;

    input.value = max;

    // Show a brief inline message next to the input
    var container = input.closest('.cart-qty-btns') || input.closest('.quantity') || input.parentElement;
    if (container) {
      var existing = container.querySelector('.dcg-qty-cap-msg');
      if (!existing) {
        existing = document.createElement('span');
        existing.className = 'dcg-qty-cap-msg';
        existing.style.cssText = [
          'display:block',
          'font-size:11px',
          'color:var(--dcg-orange,#FF7A00)',
          'margin-top:3px',
          'font-weight:600',
          'white-space:nowrap'
        ].join(';');
        container.appendChild(existing);
      }
      existing.textContent = 'Max: ' + max;
      clearTimeout(existing._dcgTimer);
      existing._dcgTimer = setTimeout(function () {
        if (existing.parentNode) existing.parentNode.removeChild(existing);
      }, 3000);
    }

    return true;
  }

  function updateStepperState(input) {
    var stepper = input.closest ? input.closest('.cart-qty-btns')
      : (function () { var el = input.parentElement; while (el && !el.classList.contains('cart-qty-btns')) el = el.parentElement; return el; })();
    if (!stepper) return;
    var val = parseInt(input.value, 10) || 0;
    var max = getMax(input);
    var atMax  = max !== Infinity && val >= max;
    var atZero = val === 0;
    stepper.classList.toggle('qty-is-zero', atZero);
    stepper.classList.toggle('qty-is-max',  atMax && !atZero);
    var inc = stepper.querySelector('.inc, .qtyplus');
    if (inc) {
      inc.classList.toggle('qty-at-max', atMax);
      if (atMax) inc.setAttribute('aria-disabled', 'true');
      else inc.removeAttribute('aria-disabled');
    }
  }

  /* ─────────────────────────────────────────────────────────
     Cart stepper intercept — capture phase, runs before shop.js
     shop.js's delegated handler calls $(".update-qty").trigger('click')
     on every +/- press, causing an instant full-page cart refresh.
     We intercept ALL .cart-qty-btns .button clicks here, handle the
     value change ourselves, then stopImmediatePropagation so shop.js
     never fires. Qty changes are only applied when the user manually
     clicks the Update Cart button.
     ───────────────────────────────────────────────────────── */
  function bindCartStepperIntercept() {
    if (document._dcgStepperBound) return;
    document._dcgStepperBound = true;

    document.addEventListener('click', function (e) {
      var btn = e.target;
      if (!btn) return;

      // Only intercept within .template-cart .cart-qty-btns
      if (!btn.closest('.template-cart .cart-qty-btns')) return;

      var isMinus = btn.classList.contains('qtyminus') || btn.classList.contains('dec');
      var isPlus  = btn.classList.contains('qtyplus')  || btn.classList.contains('inc');
      if (!isMinus && !isPlus) return;

      // Block shop.js from running its handler and auto-submitting
      e.stopImmediatePropagation();

      var inp = btn.parentElement && btn.parentElement.querySelector('.quantity, .cart__qty-input');
      if (!inp) return;

      var v   = parseInt(inp.value, 10) || 0;
      var max = getMax(inp);

      if (isMinus) {
        if (v <= 0) { updateStepperState(inp); return; }
        inp.value = String(v - 1);
      } else {
        if (v >= max) { updateStepperState(inp); return; }
        inp.value = String(v + 1);
      }

      // Fire change (for clamp guards) but NOT the auto-submit
      inp.dispatchEvent(new Event('change', { bubbles: true }));
      updateStepperState(inp);

    }, true); // capture phase — runs before jQuery delegated listeners
  }

  // Keep legacy patch functions as no-ops so existing call sites don't error
  function patchMinus(btn) { /* handled by bindCartStepperIntercept */ }
  function patchPlus(btn)  { /* handled by bindCartStepperIntercept */ }

  /* ─────────────────────────────────────────────────────────
     §3  PAYMENT BUTTONS — DOM RESTRUCTURING
     ─────────────────────────────────────────────────────────
     Instead of fighting the SDK's flex-direction:column, we
     take ownership of the layout by:
       1. Finding all .shopify-cleanslate elements inside
          .additional-checkout-buttons
       2. Creating a new wrapper div with inline flex-row style
       3. Moving those elements into our wrapper
       4. The SDK never modifies OUR wrapper, so we always win.
     ───────────────────────────────────────────────────────── */

  var _restructured = false;
  var _restructureObs = null;
  var _restructureDebounce = null;

  function restructurePaymentButtons() {
    var container = document.querySelector('.additional-checkout-buttons');
    if (!container) return false;

    /* Find all direct .shopify-cleanslate children at any nesting depth */
    var cleanslates = container.querySelectorAll('.shopify-cleanslate');
    if (!cleanslates.length) return false;

    /* If there's only one cleanslate that contains BOTH buttons internally
       (the hashed-UL structure), we work with the top-level cleanslate only */

    /* Check if we already built our row */
    var existingRow = container.querySelector('.dcg-pay-row');
    if (existingRow) {
      /* Row exists — make sure all cleanslates are still inside it */
      var allInRow = true;
      cleanslates.forEach(function (cs) {
        if (!existingRow.contains(cs) && cs.parentNode !== existingRow) allInRow = false;
      });
      if (allInRow) return true; /* already done */
      /* Otherwise fall through and rebuild */
      existingRow.parentNode && existingRow.parentNode.removeChild(existingRow);
    }

    /* Get the DIRECT children of .additional-checkout-buttons.
       The SDK wraps everything in one div, or places cleanslates directly. */
    var directChildren = Array.from(container.children);
    if (!directChildren.length) return false;

    /* Build our row container using INLINE styles (immune to SDK's CSS) */
    var row = document.createElement('div');
    row.className = 'dcg-pay-row';
    row.style.cssText = [
      'display:flex',
      'flex-direction:row',
      'flex-wrap:nowrap',
      'align-items:stretch',
      'gap:8px',
      'width:100%',
      'box-sizing:border-box',
      'margin-top:12px',
      'padding:8px 0'
    ].join(' !important;') + ' !important';

    /* Determine what to move:
       If there are multiple direct children → move them all into our row.
       If there's only ONE direct child (SDK outer wrapper) → look inside it
       for cleanslates and move those instead. */
    var toMove;
    if (directChildren.length >= 2) {
      toMove = directChildren;
    } else {
      /* Single SDK wrapper — check if it has multiple cleanslate children */
      var sdkWrapper = directChildren[0];
      var innerClean = Array.from(sdkWrapper.querySelectorAll(':scope > .shopify-cleanslate'));
      if (innerClean.length >= 2) {
        toMove = innerClean;
      } else {
        /* Single cleanslate containing a column UL — move it as-is,
           won't be side-by-side but at least has our padding */
        toMove = [sdkWrapper];
      }
    }

    /* Move elements into our row, setting equal flex on each */
    toMove.forEach(function (el) {
      el.style.setProperty('flex',      '1 1 0',   'important');
      el.style.setProperty('min-width', '0',        'important');
      el.style.setProperty('width',     'auto',     'important');
      row.appendChild(el);
    });

    container.appendChild(row);
    _restructured = true;
    return true;
  }

  function startPaymentObserver() {
    if (_restructureObs) return;
    if (typeof MutationObserver === 'undefined') return;

    _restructureObs = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        return m.type === 'childList' && m.addedNodes.length > 0;
      });
      if (!relevant) return;
      clearTimeout(_restructureDebounce);
      _restructureDebounce = setTimeout(function () {
        restructurePaymentButtons();
      }, 40);
    });

    var container = document.querySelector('.additional-checkout-buttons');
    if (container) {
      _restructureObs.observe(container, { childList: true, subtree: true });
    } else {
      /* Container not yet in DOM — watch body until it appears */
      _restructureObs.observe(document.body, { childList: true, subtree: false });
    }
  }

  function initPaymentFix() {
    /* Try immediately, then retry at intervals while SDK is loading */
    var delays = [0, 100, 300, 600, 1000, 1600, 2500, 4000];
    delays.forEach(function (ms) {
      setTimeout(function () {
        if (!_restructured) restructurePaymentButtons();
      }, ms);
    });
    startPaymentObserver();
  }

  /* ─────────────────────────────────────────────────────────
     §4  TYPED QUANTITY GUARD — cart page (cart-template.liquid)
     ─────────────────────────────────────────────────────────
     cart-template.liquid inputs carry max="" (inventory stock)
     and data-tags="" (limit-X product tags) set in Liquid,
     giving getEffectiveMax() both sources to clamp against.

     Three layers of protection:
       1. Real-time: clamp + flash message on every keystroke
       2. Blur:      final clamp + trigger change so AJAX picks it up
       3. Submit:    scan every qty input before the cart form posts
     ───────────────────────────────────────────────────────── */

  // Selector for the active cart layout (cart-template.liquid)
  var CART_QTY_SEL = [
    '.template-cart .cart-qty-btns .cart__qty-input',
    '.template-cart .cart-qty-btns .quantity.update-cart'
  ].join(',');

  function bindCartQtyInputs() {
    document.querySelectorAll(CART_QTY_SEL).forEach(function (inp) {
      if (inp._dcgQtyBound) return;
      inp._dcgQtyBound = true;

      // Layer 1: real-time clamp as they type
      inp.addEventListener('input', function () {
        clampCartInput(inp);
        updateStepperState(inp);
      });

      // Layer 2: final authoritative clamp on blur, then fire change
      // so the cart AJAX update gets the corrected value
      inp.addEventListener('blur', function () {
        var clamped = clampCartInput(inp);
        if (clamped) {
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        }
        updateStepperState(inp);
      });

      inp.addEventListener('change', function () {
        clampCartInput(inp);
        updateStepperState(inp);
      });

      updateStepperState(inp);
    });
  }

  // Layer 3: intercept form submission — last line of defence
  // If somehow a bad value survived typing, this catches it before POST
  function bindCartFormSubmitGuard() {
    var forms = document.querySelectorAll('form#cart, form[action="/cart"]');
    forms.forEach(function (form) {
      if (form._dcgSubmitGuard) return;
      form._dcgSubmitGuard = true;

      form.addEventListener('submit', function () {
        var blocked = false;
        form.querySelectorAll(CART_QTY_SEL.split(',').map(function (s) {
          return s.trim();
        }).join(',')).forEach(function (inp) {
          if (clampCartInput(inp)) blocked = true;
        });
        // Don't preventDefault — let the form post with corrected values.
        // The clamp already set .value, so the submitted payload is clean.
        if (blocked) {
          // Small delay so the user sees the orange cap message before page changes
          // (only matters on non-AJAX carts)
        }
      }, true);
    });
  }

  /* ─────────────────────────────────────────────────────────
     BOOT
     ───────────────────────────────────────────────────────── */
  function init() {
    initOutsideClickClose();

    if (!document.querySelector('.template-cart')) return;

    /* Qty steppers — unified intercept blocks shop.js auto-submit */
    bindCartStepperIntercept();

    /* Typed input guards — both cart layouts */
    bindCartQtyInputs();
    bindCartFormSubmitGuard();

    /* Payment buttons */
    initPaymentFix();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', function () {
    if (!_restructured) restructurePaymentButtons();
    setTimeout(function () { if (!_restructured) restructurePaymentButtons(); }, 500);
    setTimeout(function () { if (!_restructured) restructurePaymentButtons(); }, 1500);
  });

})();